// Common print function for ALL ID card templates.
//
// The card keeps its own size (54x86, 95x65, 65x95 ...), the PAGE is A4 and the
// cards flow on it.
//
//   printIdCards("PrintDiv")                       -> engine "css"   (vector, sharp text)
//   printIdCards("PrintDiv", { engine: "image" })  -> engine "image" (cards are rendered to
//                                                    pictures first, so NO app CSS is involved)
//   printIdCards("PrintDiv", { debug: true })      -> leaves the print frame visible on screen
//                                                    so you can inspect it in DevTools
//
// Other options: { pageSize: "A4", margin: "8mm", gap: "4mm" }

import html2canvas from "html2canvas";

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

// make relative url(...) absolute so fonts/images still resolve in the print frame
const absolutizeUrls = (cssText, baseHref) =>
  cssText.replace(
    /url\(\s*(['"]?)(?!data:|https?:|\/\/|#|blob:)([^'")]+)\1\s*\)/gi,
    (match, _q, url) => {
      try {
        return `url("${new URL(url, baseHref).href}")`;
      } catch {
        return match;
      }
    },
  );

// Copies every stylesheet of the app EXCEPT its @media print rules.
// (A global "@media print { ... }" rule in the app is the usual reason for a blank preview.)
const collectAppCss = () => {
  const walk = (rules, baseHref) => {
    let out = "";
    for (const rule of Array.from(rules)) {
      if (typeof CSSMediaRule !== "undefined" && rule instanceof CSSMediaRule) {
        const media = rule.media?.mediaText || "";
        if (/\bprint\b/i.test(media)) continue; // drop print-only rules
        out += `@media ${media}{${walk(rule.cssRules, baseHref)}}`;
      } else if (
        typeof CSSImportRule !== "undefined" &&
        rule instanceof CSSImportRule
      ) {
        try {
          out += walk(
            rule.styleSheet.cssRules,
            rule.styleSheet.href || baseHref,
          );
        } catch {
          /* cross-origin import – ignored */
        }
      } else {
        out += absolutizeUrls(rule.cssText, baseHref);
      }
    }
    return out;
  };

  let css = "";
  let links = "";

  Array.from(document.styleSheets).forEach((sheet) => {
    if (sheet.disabled) return;
    try {
      css += walk(sheet.cssRules, sheet.href || document.baseURI);
    } catch {
      // cross-origin sheet (e.g. a CDN): rules are not readable, link it instead
      if (sheet.href) links += `<link rel="stylesheet" href="${sheet.href}">`;
    }
  });

  return { css, links };
};

const waitForFrameAssets = async (doc) => {
  const pending = [];

  doc.querySelectorAll('link[rel="stylesheet"]').forEach((l) => {
    if (!l.sheet) {
      pending.push(
        new Promise((r) => {
          l.addEventListener("load", r, { once: true });
          l.addEventListener("error", r, { once: true });
        }),
      );
    }
  });

  doc.querySelectorAll("img").forEach((img) => {
    if (!img.complete) {
      pending.push(
        new Promise((r) => {
          img.addEventListener("load", r, { once: true });
          img.addEventListener("error", r, { once: true });
        }),
      );
    }
  });

  await Promise.race([
    Promise.all(pending),
    new Promise((r) => setTimeout(r, 8000)),
  ]);

  try {
    await doc.fonts?.ready;
  } catch {
    /* ignore */
  }
};

const pageRules = ({ pageSize, margin, gap }) => `
  @page { size: ${pageSize}; margin: ${margin}; }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    height: auto !important;
    overflow: visible !important;
  }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .id-print-root {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    align-content: flex-start;
    justify-content: flex-start;
    gap: ${gap};
  }
  .id-print-root > * { flex-shrink: 0; break-inside: avoid; page-break-inside: avoid; }
`;

// renders `html` inside a real-sized, off-screen iframe and prints it
const printInFrame = async (html, { debug = false } = {}) => {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  // NOT display:none / visibility:hidden / 0x0 – Chrome prints those as blank pages
  iframe.style.cssText = debug
    ? "position:fixed;right:8px;bottom:8px;width:480px;height:640px;border:2px solid red;background:#fff;z-index:99999;"
    : "position:fixed;left:-10000px;top:0;width:210mm;height:297mm;border:0;background:#fff;";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  await waitForFrameAssets(doc);
  await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 200)));

  const win = iframe.contentWindow;
  if (!debug) {
    const cleanup = () => setTimeout(() => iframe.remove(), 500);
    win.onafterprint = cleanup;
    setTimeout(cleanup, 120000);
  }

  win.focus();
  win.print();
};

const getCards = (source) =>
  Array.from(source.children).filter(
    (el) => !["STYLE", "SCRIPT", "LINK"].includes(el.tagName),
  );

/* ------------------------------------------------------------------ */
/* engines                                                             */
/* ------------------------------------------------------------------ */

const printWithCss = async (source, opts) => {
  const { css, links } = collectAppCss();

  const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <base href="${window.location.origin}/" />
    <title>ID Cards</title>
    ${links}
    <style>${css}</style>
    <style>${pageRules(opts)}</style>
  </head>
  <body>
    <div class="id-print-root">${source.innerHTML}</div>
  </body>
</html>`;

  await printInFrame(html, opts);
};

const printWithImages = async (source, opts) => {
  const cards = getCards(source);
  const items = [];

  for (const el of cards) {
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) continue;

    const canvas = await html2canvas(el, {
      scale: 4, // ~380 dpi, sharp enough for 6pt text
      useCORS: true,
      allowTaint: false,
      backgroundColor: null,
      logging: false,
    });

    items.push({
      src: canvas.toDataURL("image/png"),
      w: (rect.width * 25.4) / 96, // px -> mm
      h: (rect.height * 25.4) / 96,
    });
  }

  const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>ID Cards</title>
    <style>${pageRules(opts)}</style>
  </head>
  <body>
    <div class="id-print-root">
      ${items
        .map(
          (i) =>
            `<img src="${i.src}" style="width:${i.w.toFixed(2)}mm;height:${i.h.toFixed(2)}mm;display:block;" />`,
        )
        .join("")}
    </div>
  </body>
</html>`;

  await printInFrame(html, opts);
};

/* ------------------------------------------------------------------ */
/* public api                                                          */
/* ------------------------------------------------------------------ */

export async function printIdCards(
  elementId,
  {
    engine = "css",
    pageSize = "A4",
    margin = "8mm",
    gap = "4mm",
    debug = false,
  } = {},
) {
  const source = document.getElementById(elementId);

  if (!source || getCards(source).length === 0) {
    alert("Select a batch, student and template first.");
    return;
  }

  const opts = { pageSize, margin, gap, debug };

  try {
    if (engine === "image") {
      await printWithImages(source, opts);
    } else {
      await printWithCss(source, opts);
    }
  } catch (error) {
    console.error("Print failed:", error);
    alert("Could not open the print dialog. Check the console for details.");
  }
}
